/**
 * @file This file contains the PreferencesManager class, responsible for managing
 * user preferences that do not affect gameplay, such as sound settings, language, etc.
 * It interacts with the SaveManager to persist these preferences to localStorage.
 * @module PreferencesManager
 */

import { SaveManager } from './SaveManager.js';

export class PreferencesManager {
    /**
     * @type {object}
     * @private
     */
    _preferences = {};

    /**
     * @type {SaveManager}
     * @private
     */
    _saveManager;

    /**
     * Creates an instance of PreferencesManager.
     * @param {SaveManager} saveManager - The SaveManager instance to use for persistence.
     */
    constructor(saveManager) {
        this._saveManager = saveManager;
        this.loadPreferences();
    }

    /**
     * Loads preferences from localStorage via SaveManager.
     */
    loadPreferences() {
        const savedPreferences = this._saveManager.loadPreferences();
        this._preferences = {
            enableWeatherEffects: true,
            enable3dEffects: true,
            enableColorblindMode: false,
            ...savedPreferences // Let saved values overwrite defaults
        };
    }

    /**
     * Saves current preferences to localStorage via SaveManager.
     */
    savePreferences() {
        this._saveManager.savePreferences(this._preferences);
    }

    /**
     * Gets a specific preference value.
     * @param {string} key - The key of the preference to retrieve.
     * @param {*} defaultValue - The default value to return if the preference is not set.
     * @returns {*} The preference value or the default value.
     */
    getPreference(key, defaultValue) {
        return this._preferences[key] !== undefined ? this._preferences[key] : defaultValue;
    }

    /**
     * Sets a specific preference value.
     * @param {string} key - The key of the preference to set.
     * @param {*} value - The value to set for the preference.
     */
    setPreference(key, value) {
        this._preferences[key] = value;
        this.savePreferences();
    }

    /**
     * Gets all preferences.
     * @returns {object} A copy of the current preferences object.
     */
    getAllPreferences() {
        return { ...this._preferences };
    }
}
